import React, { useEffect, useState } from "react";
import {
  CCard,
  CCardBody,
  CCardHeader,
  CRow,
  CCol,
  CBadge,
  CProgress,
  CWidgetStatsA,
  CSpinner,
  CCallout,
  CButton,
  CButtonGroup,
  CModalFooter,
  CModalBody,
  CModalTitle,
  CModalHeader,
  CModal,
} from "@coreui/react";
import { CChartBar, CChartLine, CChartPie } from "@coreui/react-chartjs";
import { cilPeople, cibAppStore, cilCalendar, cilChart } from "@coreui/icons";
import CIcon from "@coreui/icons-react";
import formatter from "src/reusables/formatter";
import { getDashboardMetrics, getMostVisitedStores, getVisitTrends } from "src/services/Analytics";
import SalesPicker from "src/components/SalesPicker";
import { format, isWeekend } from "date-fns";
import { DateRangePicker } from "react-date-range";
import ls from "local-storage";
import Moment from "moment";

function Dashboard() {
  const useWindowDimensions = () => {

    const { innerWidth: width, innerHeight: height } = window;
    const comparison = width / height;
    const [windowDimensions, setWindowDimensions] = useState({
      width,
      height,
      comparison
    });

    useEffect(() => {
      function handleResize() {
        const { innerWidth: width, innerHeight: height } = window;
        const comparison = width / height;
        setWindowDimensions({ width, height, comparison });
      }

      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, []);

    return windowDimensions;
  };

  const { height, width, comparison } = useWindowDimensions();
  const [drModal, setDrModal] = useState(false);
  const [refetch, setRefetch] = useState(false);
  const endDay = Moment(new Date()).format("YYYY-MM-DD")
  const max = new Date(endDay);
  const [dateRange, setDateRange] = useState([{
    startDate: new Date(`${Moment(new Date()).startOf('month')}`),
    endDate: new Date()
  }])

  // Get filter parameters from localStorage
  const getFilterParams = () => {
    const smd = ls.get("smd");
    const prov = ls.get("prov");
    const city = ls.get("city");
    
    return {
      // dateFrom: Moment(dateRange[0].startDate).format("YYYY-MM-DD"),
      // dateTo: Moment(dateRange[0].endDate).format("YYYY-MM-DD"),
      smdId: smd?.id && smd.id !== "999" && smd.value !== "all" ? smd.id : "0",
      provinceId: prov?.id && prov.id !== "999" && prov.value !== "all" ? prov.id : "0",
      cityId: city?.id && city.id !== "999" && city.value !== "all" ? city.id : "0",
    };
  };

  function customDayContent(day) {
    let extraDot = null;
    if (isWeekend(day)) {
      extraDot = (
        <div
          style={{
            height: '5px',
            width: '5px',
            borderRadius: '100%',
            background: 'orange',
            position: 'absolute',
            top: 2,
            right: 2
          }}
        />
      );
    }
    return (
      <div>
        <span
          style={{
            fontWeight: '600'
          }}
        >
          {format(day, 'd')}
        </span>
      </div>
    );
  }

  const [data, setData] = useState({
    metrics: {
      visits: 0,
      plannedVisits: 0,
      outstandingPayments: 0,
      overdueVisits: 0,
    },
    stockData: [],
    visitTrends: [],
    topStores: [],
    topProducts: [],
    topNoStocks: [],
  });

  const [loading, setLoading] = useState({
    metrics: true,
    stock: true,
    trends: true,
    stores: true,
    products: true,
    noStocks: true,
  });

  const [error, setError] = useState({
    metrics: null,
    stock: null,
    trends: null,
    stores: null,
    products: null,
    noStocks: null,
  });

  const [trendsPeriod, setTrendsPeriod] = useState("today");

  // Check if any data is still loading
  const isLoading = Object.values(loading).some((val) => val);
  // Check if any error occurred
  const hasError = Object.values(error).some((val) => val !== null);

  // Fetch all dashboard data
  useEffect(() => {
    const fetchData = async () => {
      const filterParams = getFilterParams();
      
      try {
        // Fetch metrics
        setLoading((prev) => ({ ...prev, metrics: true }));
        setError((prev) => ({ ...prev, metrics: null }));
        const metricsResponse = await fetchDashboardMetrics(filterParams);
        setData((prev) => ({ ...prev, metrics: metricsResponse }));
      } catch (err) {
        console.error("Error fetching metrics:", err);
        setError((prev) => ({ ...prev, metrics: err.message }));
      } finally {
        setLoading((prev) => ({ ...prev, metrics: false }));
      }

      try {
        // Fetch stock data
        setLoading((prev) => ({ ...prev, stock: true }));
        setError((prev) => ({ ...prev, stock: null }));
        const stockResponse = await getStockSummary(filterParams);
        setData((prev) => ({ ...prev, stockData: stockResponse }));
      } catch (err) {
        console.error("Error fetching stock data:", err);
        setError((prev) => ({ ...prev, stock: err.message }));
      } finally {
        setLoading((prev) => ({ ...prev, stock: false }));
      }

      try {
        // Fetch top stores
        setLoading((prev) => ({ ...prev, stores: true }));
        setError((prev) => ({ ...prev, stores: null }));
        const topStoresResponse = await fetchTopStores(filterParams);
        console.log("Top stores data:", topStoresResponse);
        setData((prev) => ({ ...prev, topStores: topStoresResponse.stores, topProducts: topStoresResponse.stocks, topNoStocks: topStoresResponse.noStocks }));
      } catch (err) {
        console.error("Error fetching top stores:", err);
        setError((prev) => ({ ...prev, stores: err.message }));
      } finally {
        setLoading((prev) => ({ ...prev, stores: false }));
      }
    };

    fetchData();
  }, [refetch]); // Add refetch as dependency

  // Separate effect for visit trends to handle period changes
  useEffect(() => {
    const fetchVisitTrendsData = async () => {
      const filterParams = getFilterParams();
      
      try {
        setLoading((prev) => ({ ...prev, trends: true }));
        setError((prev) => ({ ...prev, trends: null }));
        const trendsResponse = await fetchVisitTrends(trendsPeriod, filterParams);
        console.log("Visit trends data:", trendsResponse);
        setData((prev) => ({ ...prev, visitTrends: trendsResponse }));
      } catch (err) {
        console.error("Error fetching visit trends:", err);
        setError((prev) => ({ ...prev, trends: err.message }));
      } finally {
        setLoading((prev) => ({ ...prev, trends: false }));
      }
    };

    fetchVisitTrendsData();
  }, [trendsPeriod, refetch]); // Add refetch as dependency

  // Effect to refetch data when filters change
  useEffect(() => {
    if (refetch) {
      setRefetch(false);
    }
  }, [refetch]);

  // Effect to listen for localStorage changes and trigger refetch
  useEffect(() => {
    const handleStorageChange = () => {
      setRefetch(true);
    };

    // Listen for storage events (when localStorage changes in other tabs)
    window.addEventListener('storage', handleStorageChange);
    
    // Also check for changes periodically (for same-tab changes)
    const interval = setInterval(() => {
      const currentSmd = ls.get("smd");
      const currentProv = ls.get("prov");
      const currentCity = ls.get("city");
      
      // Store current values to compare
      if (!window.lastFilterValues) {
        window.lastFilterValues = { smd: currentSmd, prov: currentProv, city: currentCity };
      } else {
        const lastValues = window.lastFilterValues;
        if (
          JSON.stringify(currentSmd) !== JSON.stringify(lastValues.smd) ||
          JSON.stringify(currentProv) !== JSON.stringify(lastValues.prov) ||
          JSON.stringify(currentCity) !== JSON.stringify(lastValues.city)
        ) {
          window.lastFilterValues = { smd: currentSmd, prov: currentProv, city: currentCity };
          setRefetch(true);
        }
      }
    }, 1000); // Check every second

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  const handleTrendsPeriodChange = (period) => {
    setTrendsPeriod(period);
  };

  const getTrendsTitle = () => {
    switch (trendsPeriod) {
      case "today":
        return "Today's Visit Trends (Hourly)";
      case "7days":
        return "Visit Trends (Last 7 Days)";
      case "biweekly":
        return "Visit Trends (Last 14 Days)";
      case "month":
        return "Visit Trends (Last 30 Days)";
      default:
        return "Visit Trends";
    }
  };

  // if (isLoading && !hasError) {
  //   return (
  //     <div className="d-flex justify-content-center align-items-center" style={{ height: "80vh" }}>
  //       <div className="text-center">
  //         <CSpinner color="primary" size="lg" />
  //         <p className="mt-3">Loading dashboard data...</p>
  //       </div>
  //     </div>
  //   );
  // }

  return (
    <div style={{ color: "black" }}>
      {/* <CRow style={{ overflow: "scroll" }}>
        <CCol>
          <CButton
            className="ml-3 mb-3 btn btn-dark"
            style={{ backgroundColor: "#4E5B97", border: "none" }}
            hover="true"
            onClick={() => {
              setDrModal(!drModal);
            }}
          >
            <div>
              {Moment(dateRange[0].startDate).format("ll")}
              {" - "}
              {Moment(dateRange[0].endDate).format("ll")}
            </div>
          </CButton>
        </CCol>
      </CRow>
      <CModal
        visible={drModal}
        // style={{
        //   width: comparison < 1 ? "" : "fit-content",
        //   overflowX: comparison < 1 ? "scroll" : "visible"
        // }}
      >
        <CModalHeader
          style={{
            width: comparison < 1 ? `${85 * 1 / comparison}vw` : ""
          }}
        >
          <CModalTitle>Date Range</CModalTitle>
        </CModalHeader>
        <CModalBody>
          <CCol xs="12" lg="10">
            <DateRangePicker
              onChange={
                (item) => {
                  setDateRange([item.range1])
                  ls.set("dateRange", item.range1)
                }
              }
              showSelectionPreview={true}
              moveRangeOnFirstSelection={false}
              months={1}
              maxDate={max}
              ranges={dateRange}
              direction="vertical"
              weekStartsOn={1}
              calendarFocus="backwards"
              preventSnapRefocus={true}
              rangeColors={['#4E5B97']}
              dayContentRenderer={customDayContent}
            />
          </CCol>
        </CModalBody>
        <CModalFooter
          style={{
            width: comparison < 1 ? `${85 * 1 / comparison}vw` : ""
          }}
        >
          <CButton style={{ backgroundColor: "#4E5B97", border: "none" }} onClick={() => setDrModal(!drModal)}>
            Close
          </CButton>
          <CButton style={{ backgroundColor: "#4E5B97", border: "none" }} onClick={() => {
            setRefetch(true)
            setDrModal(false)
          }}>
            OK
          </CButton>
        </CModalFooter>
      </CModal> */}
      <SalesPicker onFilterChange={() => setRefetch(true)} />
      
      {/* Filter Summary */}
      <CRow className="mb-3">
        <CCol>
          <CCard>
            <CCardBody className="py-2">
              <div className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center">
                  <strong className="me-3">Active Filters:</strong>
                  <div className="d-flex gap-2 flex-wrap">
                    <CBadge color="info">
                      Date: {Moment(dateRange[0].startDate).format("MMM DD")} - {Moment(dateRange[0].endDate).format("MMM DD, YYYY")}
                    </CBadge>
                    {ls.get("smd")?.id && ls.get("smd").id !== "999" && ls.get("smd").value !== "all" && (
                      <CBadge color="primary">
                        SMD: {ls.get("smd").label}
                      </CBadge>
                    )}
                    {ls.get("prov")?.id && ls.get("prov").id !== "999" && ls.get("prov").value !== "all" && (
                      <CBadge color="success">
                        Province: {ls.get("prov").label}
                      </CBadge>
                    )}
                    {ls.get("city")?.id && ls.get("city").id !== "999" && ls.get("city").value !== "all" && (
                      <CBadge color="warning">
                        City: {ls.get("city").label}
                      </CBadge>
                    )}
                  </div>
                </div>
                <div className="d-flex align-items-center gap-2">
                  {isLoading && (
                    <div className="d-flex align-items-center">
                      <CSpinner size="sm" className="me-2" />
                      <small className="text-muted">Updating data...</small>
                    </div>
                  )}
                  <CButton
                    size="sm"
                    color="outline-secondary"
                    onClick={() => {
                      // Reset date range to current month
                      const newDateRange = [{
                        startDate: new Date(`${Moment(new Date()).startOf('month')}`),
                        endDate: new Date()
                      }];
                      setDateRange(newDateRange);
                      ls.set("dateRange", newDateRange[0]);
                      
                      // Reset SMD/Province/City filters
                      ls.set("smd", {
                        id: "0",
                        label: "All SMD",
                        value: "all",
                      });
                      ls.set("prov", {
                        id: 0,
                        label: "All Provinces",
                        value: "all",
                      });
                      ls.set("city", {
                        id: 0,
                        label: "All Cities",
                        value: "all",
                      });
                      
                      setRefetch(true);
                    }}
                  >
                    Clear All Filters
                  </CButton>
                </div>
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
      
      {/* Top Metrics Cards */}
      <CRow className="mb-4">
        <CCol md={3}>
          <CWidgetStatsA
            color="primary"
            value={data.metrics.visits}
            title="Today's Visits"
            icon={<CIcon icon={cilPeople} height={36} />}
          />
        </CCol>
        <CCol md={3}>
          <CWidgetStatsA
            color="info"
            value={data.metrics.plannedVisits}
            title="Planned Visits"
            icon={<CIcon icon={cilCalendar} height={36} />}
          />
        </CCol>
        <CCol md={3}>
          <CWidgetStatsA
            color="warning"
            value={formatter("currency", data.metrics.outstandingPayments)}
            title="Outstanding Payments"
            icon={<CIcon icon={cilChart} height={36} />}
          />
        </CCol>
        <CCol md={3}>
          <CWidgetStatsA
            color="danger"
            value={data.metrics.overdueVisits}
            title="Overdue Visits"
            icon={<CIcon icon={cibAppStore} height={36} />}
          />
        </CCol>
      </CRow>

      {/* Stock Availability Summary */}
      {!loading.stock && (
        <CCard className="mb-4">
          <CCardHeader>
            <h5>Stock Availability Summary</h5>
          </CCardHeader>
          <CCardBody>
            <CRow className="g-3">
              {data.stockData.map((category) => (
                <CCol md={6} lg={3} key={category.categoryID}>
                  <CCard
                    className="h-100"
                    style={{
                      borderLeft: `4px solid ${
                        category.availabilityPercentage >= 50
                          ? "#2eb85c"
                          : category.availabilityPercentage >= 25
                          ? "#f9b115"
                          : "#e55353"
                      }`,
                    }}
                  >
                    <CCardBody>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <h6>{category.categoryName}</h6>
                        <CBadge
                          color={
                            category.availabilityPercentage >= 50
                              ? "success"
                              : category.availabilityPercentage >= 25
                              ? "warning"
                              : "danger"
                          }
                        >
                          {category.availabilityPercentage}%
                        </CBadge>
                      </div>
                      <div className="mb-2">
                        <small className="text-muted">
                          {category.availableProducts} of{" "}
                          {category.totalProducts} products available
                        </small>
                      </div>
                      <CProgress
                        color={
                          category.availabilityPercentage >= 50
                            ? "success"
                            : category.availabilityPercentage >= 25
                            ? "warning"
                            : "danger"
                        }
                        value={category.availabilityPercentage}
                        className="mb-3"
                      />
                      <div className="d-flex justify-content-between">
                        <small>Low: {category.lowStockProducts}</small>
                        <small>Out: {category.outOfStockProducts}</small>
                      </div>
                    </CCardBody>
                  </CCard>
                </CCol>
              ))}
            </CRow>
          </CCardBody>
        </CCard>
      )}

      {/* Visit Trends */}
      <CRow className="mb-4">
        <CCol>
        <CCard>
          <CCardHeader className="d-flex justify-content-between align-items-center">
            <h5>{getTrendsTitle()}</h5>
            <CButtonGroup>
              <CButton
                color="outline-secondary"
                size="sm"
                active={trendsPeriod === "today"}
                onClick={() => handleTrendsPeriodChange("today")}
              >
                Today
              </CButton>
              <CButton
                color="outline-secondary"
                size="sm"
                active={trendsPeriod === "7days"}
                onClick={() => handleTrendsPeriodChange("7days")}
              >
                7 Days
              </CButton>
              <CButton
                color="outline-secondary"
                size="sm"
                active={trendsPeriod === "biweekly"}
                onClick={() => handleTrendsPeriodChange("biweekly")}
              >
                14 Days
              </CButton>
              <CButton
                color="outline-secondary"
                size="sm"
                active={trendsPeriod === "month"}
                onClick={() => handleTrendsPeriodChange("month")}
              >
                30 Days
              </CButton>
            </CButtonGroup>
          </CCardHeader>
          <CCardBody>
            {loading.trends ? (
              <div className="text-center py-4">
                <CSpinner />
              </div>
            ) : (
              <CChartLine
                height={"100px"}
                data={{
                  labels: data.visitTrends.map((t) => t.date),
                  datasets: [
                    {
                      label: "Completed Visits",
                      backgroundColor: "rgba(78, 91, 151, 0.2)",
                      borderColor: "#4E5B97",
                      pointBackgroundColor: "#4E5B97",
                      pointBorderColor: "#fff",
                      data: data.visitTrends.map((t) => t.completed),
                    },
                    {
                      label: "Planned Visits",
                      backgroundColor: "rgba(48, 111, 186, 0.2)",
                      borderColor: "#de148c",
                      pointBackgroundColor: "#306fba",
                      pointBorderColor: "#fff",
                      data: data.visitTrends.map((t) => t.planned),
                    },
                  ],
                }}
                options={{
                  scales: {
                    y: {
                      beginAtZero: true,
                      ticks: {
                        precision: 0,
                      },
                    },
                  },
                }}
              />
            )}
          </CCardBody>
        </CCard>
        </CCol>
      </CRow>
      <CRow className="mb-4">
        <CCol md={4}>
          <CCard className="h-100">
            <CCardHeader>
              <h5>Top 10 Visited Stores</h5>
            </CCardHeader>
            <CCardBody>
              {loading.stores ? (
                <div className="text-center py-4">
                  <CSpinner />
                </div>
              ) : data.topStores.length > 0 ? (
                data.topStores.map((store, index) => (
                  <a
                    href={"/stores/details/" + store.store_id}
                    key={store.store_id}
                    className="text-decoration-none text-dark"
                  >
                    <div key={store.store_id} className="mb-3">
                      <div className="d-flex justify-content-between">
                        <span>
                          <CBadge color="primary" className="me-2">
                            {index + 1}
                          </CBadge>
                          {store.storeName}
                        </span>
                        <CBadge color="secondary">
                          {store.visitCount} visits
                        </CBadge>
                      </div>
                    </div>
                  </a>
                ))
              ) : (
                <div className="text-center text-muted py-4">
                  No store data available
                </div>
              )}
            </CCardBody>
          </CCard>
        </CCol>
        <CCol md={4}>
          <CCard className="h-100">
            <CCardHeader>
              <h5>Top 10 Stocked Products</h5>
            </CCardHeader>
            <CCardBody>
              {loading.stores ? (
                <div className="text-center py-4">
                  <CSpinner />
                </div>
              ) : data.topProducts.length > 0 ? (
                data.topProducts.map((stock, index) => (
                  <a
                    href={"/product/details/" + stock.id}
                    key={stock.id}
                    className="text-decoration-none text-dark"
                  >
                    <div key={stock.id} className="mb-3">
                      <div className="d-flex justify-content-between">
                        <span>
                          <CBadge color="primary" className="me-2">
                            {index + 1}
                          </CBadge>
                          {stock.product_name}
                        </span>
                        <CBadge color="secondary">
                          {stock.storeCount} stores
                        </CBadge>
                      </div>
                    </div>
                  </a>
                ))
              ) : (
                <div className="text-center text-muted py-4">
                  No store data available
                </div>
              )}
            </CCardBody>
          </CCard>
        </CCol>
        <CCol md={4}>
          <CCard className="h-100">
            <CCardHeader>
              <h5>Top 10 Out of Stock Products</h5>
            </CCardHeader>
            <CCardBody>
              {loading.stores ? (
                <div className="text-center py-4">
                  <CSpinner />
                </div>
              ) : data.topNoStocks.length > 0 ? (
                data.topNoStocks.map((noStock, index) => (
                  <a
                    href={"/product/details/" + noStock.id}
                    key={noStock.id}
                    className="text-decoration-none text-dark"
                  >
                    <div key={noStock.id} className="mb-3">
                      <div className="d-flex justify-content-between">
                        <span>
                          <CBadge color="primary" className="me-2">
                            {index + 1}
                          </CBadge>
                          {noStock.product_name}
                        </span>
                        <CBadge color="secondary">
                          {noStock.storeCount} stores
                        </CBadge>
                      </div>
                    </div>
                  </a>
                ))
              ) : (
                <div className="text-center text-muted py-4">
                  No store data available
                </div>
              )}
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      {/* Stock Details by Category */}
      {!loading.stock && data.stockData.length > 0 && (
        <CCard className="mb-4">
          <CCardHeader>
            <h5>Stock Details by Category</h5>
          </CCardHeader>
          <CCardBody>
            {data.stockData.map((category) => (
              <div key={category.categoryID} className="mb-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6>{category.categoryName}</h6>
                  <CBadge color="info">
                    {category.products.length} products
                  </CBadge>
                </div>
                <CRow className="g-2">
                  {category.products.slice(0, 4).map((product) => (
                    <CCol md={3} key={product.productID}>
                      <div className="p-3 border rounded">
                        <div className="d-flex justify-content-between">
                          <strong>{product.productName}</strong>
                          <CBadge
                            color={product.stock > 0 ? "success" : "danger"}
                          >
                            {product.stock}
                          </CBadge>
                        </div>
                        <small
                          className={
                            product.stock > 0 ? "text-success" : "text-danger"
                          }
                        >
                          {product.stock > 0 ? "In Stock" : "Out of Stock"}
                        </small>
                      </div>
                    </CCol>
                  ))}
                  {category.products.length > 4 && (
                    <CCol md={3}>
                      <div className="p-3 border rounded text-center">
                        <strong>+{category.products.length - 4} more</strong>
                      </div>
                    </CCol>
                  )}
                </CRow>
              </div>
            ))}
          </CCardBody>
        </CCard>
      )}
    </div>
  );
}

// API functions
async function fetchDashboardMetrics(params) {
  try {
    const response = await getDashboardMetrics(params);
    if (response.success) {
      return (
        response.metrics || {
          visits: 0,
          plannedVisits: 0,
          outstandingPayments: 0,
          overdueVisits: 0,
        }
      );
    }
    throw new Error(response.message || "Failed to fetch metrics");
  } catch (error) {
    console.error("Error in fetchDashboardMetrics:", error);
    throw error;
  }
}

async function getStockSummary(params) {
  return [
    {
      categoryID: 1,
      categoryName: "Product",
      totalProducts: 24,
      availableProducts: 7,
      availabilityPercentage: 29,
      lowStockProducts: 3,
      outOfStockProducts: 12,
      products: [
        { productID: 43, productName: "Icelands Strawberry", stock: 2, isAvailable: 1 },
        // ... other products
      ],
    },
    // ... other categories
  ];
}

async function fetchVisitTrends(period = "today", params) {
  try {
    const response = await getVisitTrends(period, params);
    if (response.success) {
      return response.data || [];
    }
    throw new Error(response.message || "Failed to fetch visit trends");
  } catch (error) {
    console.error("Error in fetchVisitTrends:", error);
    throw error;
  }
}

async function fetchTopStores(params) {
  try {
    const response = await getMostVisitedStores(params);
    if (response.success) {
      return response || [];
    }
    throw new Error(response.message || "Failed to fetch most visited stores");
  } catch (error) {
    console.error("Error in fetchVisitTrends:", error);
    throw error;
  }
}

export default Dashboard;
